import React, { useState, useEffect, useCallback } from "react";
import ScamActivityAPI from "../services/ScamActivityAPI";
import ScamActivityMapSearch from "./ScamActivityMapSearch";
import ScamActivityListView from "./ScamActivityListView";
import ScamActivityMapView from "./ScamActivityMapView";
import LoadingSpinner from "../common/LoadingSpinner";
import "../css/Common.css";
import "./css/ScamActivityMap.css";

const VIEW_MAP = "map";
const VIEW_LIST = "list";

/**
 * Main container for the Scam Activity Map feature.
 * Lives inside the Alerts Intelligence Hub as the "scammap" view.
 *
 * Owns all state:
 *  - Search parameters
 *  - API results
 *  - View toggle (map | list)
 *  - Saved address
 *
 * @param {Function} goBack - Navigate back to the Alerts Hub dashboard
 */
function ScamActivityMap({ goBack }) {
  // ── Results state ──────────────────────────────────────────────────────────
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── View mode ──────────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState(VIEW_LIST);

  // ── Saved address ──────────────────────────────────────────────────────────
  const [savedAddress, setSavedAddress] = useState("");
  const [savedAddressLoading, setSavedAddressLoading] = useState(true);

  // ── Load saved address on mount ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await ScamActivityAPI.getSavedAddress();
        setSavedAddress(res?.savedAddress || "");
      } catch {
        // Non-critical — search still works without a saved address
      } finally {
        setSavedAddressLoading(false);
      }
    })();
  }, []);

  // ── Search handler ─────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ScamActivityAPI.search(params);
      setResults(data);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Search failed. Please check the address and try again.";
      setError(msg);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Save address handler ───────────────────────────────────────────────────
  const handleSaveAddress = useCallback(async (address) => {
    try {
      await ScamActivityAPI.saveAddress(address);
      setSavedAddress(address);
    } catch {
      // Silently fail — saving is best-effort
    }
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  const hasResults = results && results.incidents;

  return (
    <div className="sam-container common-container">
      {/* ── Header ── */}
      <div className="common-header sam-header">
        <div className="sam-header__back-row">
          <button
            className="aih-back-button"
            onClick={goBack}
            aria-label="Back to Alerts Hub"
          >
            <span className="back-arrow">←</span>
            <span className="back-text">Back to Alerts Hub</span>
          </button>
        </div>
        <div className="sam-header__title-row">
          <div>
            <h1 className="main-title">📍 Scam Activity Near You</h1>
            <p className="common-subtitle">
              See scams reported by your neighbors within a chosen radius. All
              results are anonymized — no victim information is shown.
            </p>
          </div>
        </div>
      </div>

      {/* ── Body: search panel + results ── */}
      <div className="sam-body">
        {/* Left / top: search controls */}
        <div className="sam-body__search-panel">
          {savedAddressLoading ? (
            <LoadingSpinner message="Loading your saved address…" />
          ) : (
            <ScamActivityMapSearch
              savedAddress={savedAddress}
              loading={loading}
              onSearch={handleSearch}
              onSaveAddress={handleSaveAddress}
            />
          )}
        </div>

        {/* Right / bottom: results */}
        <div className="sam-body__results">
          {/* Loading */}
          {loading && (
            <div className="sam-results-loading">
              <LoadingSpinner message="Searching for scams near you…" />
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="sam-results-error">
              <span className="sam-results-error__icon">⚠️</span>
              <p>{error}</p>
            </div>
          )}

          {/* Empty initial state */}
          {!loading && !error && !hasResults && (
            <div className="sam-results-empty">
              <div className="sam-results-empty__icon">🗺️</div>
              <h3>Enter a city and state to get started</h3>
              <p>
                Type a city and select a state above, set the radius and time
                period, and we'll show you scams reported nearby.
              </p>
              <ul className="sam-results-empty__tips">
                <li>
                  📍 Results show approximate locations only — no victim
                  addresses
                </li>
                <li>📅 Data is updated nightly from the BBB Scam Tracker</li>
                <li>🔒 Your search is private and not shared</li>
              </ul>
            </div>
          )}

          {/* Results */}
          {!loading && !error && hasResults && (
            <>
              {/* ── Type breakdown pills ── */}
              {results.countByScamType &&
                Object.keys(results.countByScamType).length > 0 && (
                  <div className="sam-type-breakdown">
                    {Object.entries(results.countByScamType)
                      .sort(([, a], [, b]) => b - a)
                      .map(([type, count]) => (
                        <span key={type} className="sam-type-pill">
                          {type}{" "}
                          <span className="sam-type-pill__count">{count}</span>
                        </span>
                      ))}
                  </div>
                )}

              {/* ── View toggle ── */}
              <div
                className="sam-view-toggle"
                role="group"
                aria-label="Switch view"
              >
                <button
                  className={`sam-view-toggle__btn${viewMode === VIEW_LIST ? " sam-view-toggle__btn--active" : ""}`}
                  onClick={() => setViewMode(VIEW_LIST)}
                  aria-pressed={viewMode === VIEW_LIST}
                >
                  ☰ List View
                </button>
                <button
                  className={`sam-view-toggle__btn${viewMode === VIEW_MAP ? " sam-view-toggle__btn--active" : ""}`}
                  onClick={() => setViewMode(VIEW_MAP)}
                  aria-pressed={viewMode === VIEW_MAP}
                >
                  🗺️ Map View
                </button>
              </div>

              {/* ── List or Map ── */}
              {viewMode === VIEW_LIST && (
                <ScamActivityListView
                  incidents={results.incidents}
                  totalCount={results.totalCount}
                  radiusMiles={results.radiusMiles}
                  dateRange={results.dateRange}
                  resolvedAddress={results.resolvedAddress}
                />
              )}
              {viewMode === VIEW_MAP && (
                <div className="sam-map-wrapper">
                  <ScamActivityMapView
                    incidents={results.incidents}
                    searchCenter={results.searchCenter}
                    radiusMiles={results.radiusMiles}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScamActivityMap;
